import type { SVGProps } from 'react';
import { NavLink } from 'react-router-dom';
import externalLinkIcon from '@/assets/svg/external-link.svg';
import { normalizeExternalUrl } from '@/utils/externalUrl';

const MenuIcon = ({ children, ...props }: SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    {...props}
  >
    {children}
  </svg>
);

const linkClass =
  'flex w-full shrink-0 items-center gap-3 px-4 py-3 text-left text-gray-500 no-underline transition hover:bg-gray-100 hover:text-[#631d76] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#631d76]';

const kdRequestsUrl = normalizeExternalUrl(process.env.REACT_APP_KD_REQUESTS_URL);

const portalMenuItems = [
  {
    title: 'Заявки на подбор',
    href: '/hr/list',
    iconPath:
      'M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25Z',
  },
  {
    title: 'Отчеты',
    href: '/hr/report',
    iconPath:
      'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z',
  },
  {
    title: 'Инструкция',
    href: '/hr/instruction.pdf',
    newTab: true,
    iconPath:
      'm11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z',
  },
  {
    title: 'Шаблоны',
    href: '/hr/templates',
    iconPath:
      'M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H8.25m7.5 10.375h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876V6.75c0 .621.504 1.125 1.125 1.125h3.375',
  },
] as const;

type PortalNavigationProps = {
  isRecruiter: boolean;
  canViewExternalLink: boolean;
  onNavigate?: () => void;
};

const PortalNavigation = ({
  isRecruiter,
  canViewExternalLink,
  onNavigate,
}: PortalNavigationProps) => (
  <nav
    className="flex min-h-0 w-full flex-1 flex-col gap-3 overflow-y-auto py-4"
    aria-label="Основное меню"
  >
    <NavLink
      to="/"
      end
      onClick={onNavigate}
      className={({ isActive }) => `${linkClass} ${isActive ? 'bg-gray-200 text-[#631d76]' : ''}`}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center">
        <MenuIcon className="h-7 w-7" aria-hidden="true">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 10.8 12 3l9 7.8V21a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1V10.8Z"
          />
        </MenuIcon>
      </span>
      <span className="whitespace-nowrap text-base">Главная</span>
    </NavLink>

    {portalMenuItems.map((item) => (
      <a
        key={item.title}
        href={item.href}
        target={'newTab' in item && item.newTab ? '_blank' : undefined}
        rel={'newTab' in item && item.newTab ? 'noreferrer' : undefined}
        className={linkClass}
        onClick={onNavigate}
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center">
          <MenuIcon className="h-7 w-7" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d={item.iconPath} />
          </MenuIcon>
        </span>
        <span className="whitespace-nowrap text-base">{item.title}</span>
      </a>
    ))}

    {isRecruiter && (
      <NavLink
        to="/candidates"
        onClick={onNavigate}
        className={({ isActive }) => `${linkClass} ${isActive ? 'bg-gray-200 text-[#631d76]' : ''}`}
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center">
          <MenuIcon className="h-7 w-7" aria-hidden="true">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Z"
            />
          </MenuIcon>
        </span>
        <span className="whitespace-nowrap text-base">Кандидаты</span>
      </NavLink>
    )}

    {canViewExternalLink && kdRequestsUrl && (
      <div className="mt-auto w-full shrink-0 border-t border-gray-200 pt-4">
        <h2 className="px-4 text-xs font-medium uppercase text-gray-400">Внешние ссылки</h2>
        <a
          href={kdRequestsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass}
          onClick={onNavigate}
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center">
            <span
              className="block h-7 w-7 bg-current"
              style={{
                mask: `url(${externalLinkIcon}) center / contain no-repeat`,
                WebkitMask: `url(${externalLinkIcon}) center / contain no-repeat`,
              }}
              aria-hidden="true"
            />
          </span>
          <span className="whitespace-nowrap text-base">Кадровые заявки КД</span>
        </a>
      </div>
    )}
  </nav>
);

export default PortalNavigation;
